import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  TestTube, 
  ChartLine, 
  Brain, 
  Heart,
  ShieldCheck,
  Users,
  ArrowRight,
  Check,
  Sparkle,
  Heartbeat,
  Moon,
  Barbell,
  Wine,
  FacebookLogo,
  AppleLogo,
  GooglePlayLogo,
  List,
  X
} from '@phosphor-icons/react';

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleAppDownload = (platform) => {
    toast.info(`${platform} app coming soon! We're working hard to bring MenoPath to your mobile device.`, {
      duration: 4000,
    });
  };

  const features = [
    {
      icon: TestTube,
      title: 'Hormone Analysis',
      description: 'Input your hormonal test results including FSH, Estradiol, AMH, and more to build your health profile.'
    },
    {
      icon: Heartbeat,
      title: 'Lifestyle Factors',
      description: 'We consider your sleep patterns, exercise habits, smoking status, and alcohol consumption for accurate predictions.'
    },
    {
      icon: Brain,
      title: 'AI-Powered Predictions',
      description: 'Our algorithms combine biomarkers and lifestyle data to predict your personalized menopause timeline.'
    }
  ];

  const biomarkers = [
    'Anti-Müllerian Hormone (AMH)',
    'Follicle-Stimulating Hormone (FSH)',
    'Luteinizing Hormone (LH)',
    'Estradiol (E2)',
    'Progesterone',
    'Inhibin B'
  ];

  const lifestyleFactors = [
    { icon: Moon, label: 'Sleep Quality & Duration' },
    { icon: Barbell, label: 'Exercise Frequency' },
    { icon: Heart, label: 'Smoking Status' },
    { icon: Wine, label: 'Alcohol Consumption' }
  ];

  const stats = [
    { value: '8,000+', label: 'Active Users' },
    { value: '450K+', label: 'Data Points Analyzed' },
    { value: '85%+', label: 'Accuracy Rate' }
  ];

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#5B21B6] font-['Outfit']" data-testid="nav-logo">
            MenoPath
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[#1E1B4B] hover:text-[#5B21B6] transition-colors">Features</a>
            <a href="#science" className="text-[#1E1B4B] hover:text-[#5B21B6] transition-colors">Science</a>
            <Link to="/about" className="text-[#1E1B4B] hover:text-[#5B21B6] transition-colors">About</Link>
            <Link to="/faq" className="text-[#1E1B4B] hover:text-[#5B21B6] transition-colors">FAQ</Link>
            <Link to="/education" className="text-[#1E1B4B] hover:text-[#5B21B6] transition-colors">Education</Link>
            <Link to="/contact" className="text-[#1E1B4B] hover:text-[#5B21B6] transition-colors">Contact</Link>
          </div>
          
          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-[#5B21B6] hover:bg-[#EDE9FE]" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#5B21B6] hover:bg-[#4C1D95] rounded-full px-6" data-testid="nav-register-btn">
                Request Early Access
              </Button>
            </Link>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 hover:bg-[#EDE9FE] rounded-xl transition-colors"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-[#5B21B6]" weight="bold" />
            ) : (
              <List className="w-6 h-6 text-[#5B21B6]" weight="bold" />
            )}
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              <div className="px-6 py-4 space-y-1">
                <a 
                  href="#features" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-[#1E1B4B] hover:bg-[#EDE9FE] rounded-xl transition-colors font-medium"
                >
                  Features
                </a>
                <a 
                  href="#science" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-[#1E1B4B] hover:bg-[#EDE9FE] rounded-xl transition-colors font-medium"
                >
                  Science
                </a>
                <Link 
                  to="/about" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-[#1E1B4B] hover:bg-[#EDE9FE] rounded-xl transition-colors font-medium"
                >
                  About
                </Link>
                <Link 
                  to="/faq" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-[#1E1B4B] hover:bg-[#EDE9FE] rounded-xl transition-colors font-medium"
                >
                  FAQ
                </Link>
                <Link 
                  to="/education" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-[#1E1B4B] hover:bg-[#EDE9FE] rounded-xl transition-colors font-medium"
                >
                  Education
                </Link>
                <Link 
                  to="/contact" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-[#1E1B4B] hover:bg-[#EDE9FE] rounded-xl transition-colors font-medium"
                >
                  Contact
                </Link>
                
                <div className="pt-4 mt-4 border-t border-gray-100 space-y-3">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full justify-center rounded-xl border-[#5B21B6] text-[#5B21B6]">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full justify-center bg-[#5B21B6] hover:bg-[#4C1D95] rounded-xl">
                      Request Early Access
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://customer-assets.emergentagent.com/job_wellness-forecast-7/artifacts/n5anl8a0_iStock-2244165822.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E1B4B]/80 via-[#5B21B6]/60 to-[#1E1B4B]/70" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <div className="max-w-3xl">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/30">
                <Sparkle weight="fill" className="w-4 h-4" />
                Science-Powered Insights
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] leading-tight tracking-tight">
                Bringing Clarity to the{' '}
                <span className="bg-gradient-to-r from-[#A78BFA] to-[#34D399] bg-clip-text text-transparent">
                  Menopause Timeline
                </span>
              </h1>
              <p className="mt-6 text-lg text-white/90 leading-relaxed max-w-xl">
                MenoPath combines <strong className="text-[#A78BFA]">hormone biomarkers</strong> with <strong className="text-[#34D399]">lifestyle factors</strong> like sleep, exercise, smoking, and alcohol consumption to deliver personalized menopause predictions powered by AI.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    className="bg-white text-[#5B21B6] hover:bg-white/90 rounded-full px-8 h-14 text-lg font-semibold shadow-lg shadow-black/20"
                    data-testid="hero-cta-btn"
                  >
                    Request Early Access
                    <ArrowRight className="ml-2 w-5 h-5" weight="bold" />
                  </Button>
                </Link>
                <a href="#science">
                  <Button 
                    variant="outline" 
                    className="rounded-full px-8 h-14 text-lg border-2 border-white text-white hover:bg-white/20 bg-transparent"
                  >
                    Learn More
                  </Button>
                </a>
              </div>
              
              {/* Stats */}
              <div className="mt-12 flex flex-wrap gap-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center sm:text-left">
                    <p className="text-3xl sm:text-4xl font-bold text-white font-['Outfit']">{stat.value}</p>
                    <p className="text-sm text-white/70">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <a href="#features" className="flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors">
            <span className="text-sm">Scroll to explore</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <ArrowRight className="w-5 h-5 rotate-90" weight="bold" />
            </motion.div>
          </a>
        </motion.div>
      </section>

      {/* How It Works */}
      <section id="features" className="py-24 px-6 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#EDE9FE] rounded-full blur-3xl opacity-40 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D1FAE5] rounded-full blur-3xl opacity-40 translate-x-1/2 translate-y-1/2" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto mb-20"
          >
            <p className="text-[#5B21B6] font-semibold tracking-widest uppercase text-sm mb-6">How It Works</p>
            
            {/* Visual Formula */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="flex items-center gap-3 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] px-6 py-4 rounded-2xl shadow-lg shadow-purple-200"
              >
                <TestTube className="w-7 h-7 text-white" weight="duotone" />
                <span className="text-white font-bold text-lg">Hormones</span>
              </motion.div>
              
              <motion.span 
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-light text-[#5B21B6]"
              >
                +
              </motion.span>
              
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-3 bg-gradient-to-br from-[#059669] to-[#10B981] px-6 py-4 rounded-2xl shadow-lg shadow-green-200"
              >
                <Heartbeat className="w-7 h-7 text-white" weight="duotone" />
                <span className="text-white font-bold text-lg">Lifestyle</span>
              </motion.div>
              
              <motion.span 
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-3xl font-light text-[#5B21B6]"
              >
                =
              </motion.span>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-3 bg-gradient-to-br from-[#1E1B4B] to-[#312E81] px-6 py-4 rounded-2xl shadow-lg shadow-indigo-200"
              >
                <Brain className="w-7 h-7 text-white" weight="duotone" />
                <span className="text-white font-bold text-lg">Your Prediction</span>
              </motion.div>
            </div>
            
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4"
            >
              Three Simple Steps to Clarity
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="text-[#6B7280] text-lg max-w-2xl mx-auto"
            >
              We combine your biological markers with daily habits for the most comprehensive and personalized menopause prediction.
            </motion.p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + index * 0.15 }}
                className="group relative bg-white rounded-3xl p-8 border border-gray-100 hover:border-[#5B21B6]/20 hover:shadow-2xl hover:shadow-purple-100 hover:-translate-y-2 transition-all duration-500"
              >
                {/* Step number background */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#F8F6FF] rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-2xl font-bold text-[#5B21B6] font-['Outfit']">{index + 1}</span>
                </div>
                
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-8 h-8 text-white" weight="duotone" />
                </div>
                <h3 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">{feature.title}</h3>
                <p className="text-[#6B7280] leading-relaxed">{feature.description}</p>
                
                {/* Connector line for non-last items */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 border-t-2 border-dashed border-[#5B21B6]/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Science Section */}
      <section id="science" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-[#5B21B6] font-semibold tracking-widest uppercase text-sm mb-4">The Science</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6">
                Hormones + Lifestyle = Complete Picture
              </h2>
              <p className="text-[#6B7280] text-lg leading-relaxed mb-8">
                MenoPath combines clinical hormone biomarkers with lifestyle factors that research shows can influence menopause timing by up to 2 years. Our AI analyzes both to give you the most accurate prediction possible.
              </p>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-[#1E1B4B] mb-3">Hormone Biomarkers:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {biomarkers.map((marker, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#D1FAE5] flex items-center justify-center">
                          <Check className="w-4 h-4 text-[#10B981]" weight="bold" />
                        </div>
                        <span className="text-[#1E1B4B] text-sm">{marker}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-[#1E1B4B] mb-3">Lifestyle Factors We Analyze:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {lifestyleFactors.map((factor, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#FEF3C7] flex items-center justify-center">
                          <factor.icon className="w-4 h-4 text-[#92400E]" weight="bold" />
                        </div>
                        <span className="text-[#1E1B4B] text-sm">{factor.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/7578808/pexels-photo-7578808.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Doctor consulting with patient"
                className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-8"
            >
              <ShieldCheck className="w-12 h-12 text-white mb-4" weight="duotone" />
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Privacy First</h3>
              <p className="text-white/70">Your health data is encrypted and secure. We never share your personal information.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-8"
            >
              <Heart className="w-12 h-12 text-white mb-4" weight="duotone" />
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Evidence-Based</h3>
              <p className="text-white/70">Our predictions are based on peer-reviewed research and clinical studies.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-8"
            >
              <Users className="w-12 h-12 text-white mb-4" weight="duotone" />
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Community</h3>
              <p className="text-white/70">Join thousands of women taking control of their menopause journey.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facebook Community Section */}
      <section className="py-16 px-6 bg-[#F8F6FF] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#1877F2]/10 to-[#5B21B6]/10 rounded-full blur-3xl" />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto relative z-10"
        >
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 text-center">
            {/* Facebook Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1877F2] mb-6 shadow-lg shadow-blue-200">
              <FacebookLogo className="w-10 h-10 text-white" weight="fill" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4">
              You're Not Alone on This Journey
            </h2>
            
            <p className="text-[#6B7280] text-lg mb-8 max-w-2xl mx-auto">
              Connect with <span className="font-semibold text-[#5B21B6]">thousands of women</span> sharing experiences, 
              tips, and support in our welcoming Facebook community.
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-[#1877F2] font-['Outfit']">40K+</p>
                <p className="text-sm text-[#6B7280]">Members</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#1877F2] font-['Outfit']">Daily</p>
                <p className="text-sm text-[#6B7280]">Conversations</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#1877F2] font-['Outfit']">24/7</p>
                <p className="text-sm text-[#6B7280]">Support</p>
              </div>
            </div>
            
            {/* CTA Button */}
            <a 
              href="https://www.facebook.com/menopath" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold text-lg px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-blue-300 hover:-translate-y-1"
              data-testid="facebook-community-btn"
            >
              <FacebookLogo className="w-6 h-6" weight="fill" />
              Join Our Facebook Community
              <ArrowRight className="w-5 h-5" weight="bold" />
            </a>
            
            <p className="mt-4 text-sm text-[#9CA3AF]">Free to join • Supportive environment • Expert insights</p>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6">
              Ready to Understand Your Body Better?
            </h2>
            <p className="text-lg text-[#6B7280] mb-8 max-w-2xl mx-auto">
              We're currently in beta and accepting a limited number of users. Request early access to be among the first to experience MenoPath.
            </p>
            <Link to="/register">
              <Button 
                className="bg-[#5B21B6] hover:bg-[#4C1D95] rounded-full px-10 h-14 text-lg font-semibold"
                data-testid="cta-btn"
              >
                Request Early Access
                <ArrowRight className="ml-2 w-5 h-5" weight="bold" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E1B4B] py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-2xl font-bold text-white font-['Outfit']">MenoPath</p>
              <p className="text-white/50 mt-1">Science-driven Actionable Insights</p>
              
              {/* App Download Buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleAppDownload('iOS')}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl px-4 py-2 transition-all duration-300 hover:scale-105"
                  data-testid="app-store-btn"
                >
                  <AppleLogo className="w-6 h-6 text-white" weight="fill" />
                  <div className="text-left">
                    <p className="text-[10px] text-white/60 leading-none">Download on the</p>
                    <p className="text-sm font-semibold text-white leading-tight">App Store</p>
                  </div>
                </button>
                <button
                  onClick={() => handleAppDownload('Android')}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl px-4 py-2 transition-all duration-300 hover:scale-105"
                  data-testid="google-play-btn"
                >
                  <GooglePlayLogo className="w-6 h-6 text-white" weight="fill" />
                  <div className="text-left">
                    <p className="text-[10px] text-white/60 leading-none">Get it on</p>
                    <p className="text-sm font-semibold text-white leading-tight">Google Play</p>
                  </div>
                </button>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              <a href="#features" className="text-white/70 hover:text-white transition-colors">Features</a>
              <a href="#science" className="text-white/70 hover:text-white transition-colors">Science</a>
              <Link to="/about" className="text-white/70 hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="text-white/70 hover:text-white transition-colors">Contact</Link>
              <Link to="/faq" className="text-white/70 hover:text-white transition-colors">FAQ</Link>
              <Link to="/login" className="text-white/70 hover:text-white transition-colors">Sign In</Link>
              <Link to="/terms" className="text-white/70 hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="text-white/70 hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <h4 className="text-white font-semibold mb-3 text-sm">Disclaimer:</h4>
            <p className="text-white/60 text-sm leading-relaxed">
              Menopath.ai is provided for informational and educational purposes only. It is not intended to diagnose, treat, cure, prevent, or predict any medical or health condition, nor to replace professional medical advice, diagnosis, or treatment. All results, insights, and recommendations generated by this platform are subjective, generalized, and may not be accurate or applicable to every individual. Always consult a qualified healthcare professional regarding any medical concerns or decisions. Use of this website and its content is at your own risk. Menopath.ai and its owners disclaim all liability for actions taken or not taken based on the information provided.
            </p>
          </div>
          
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-white/50 text-sm">© 2026 MenoPath. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
